if (!customElements.get('photo-personalization')) {
  customElements.define(
    'photo-personalization',
    class PhotoPersonalization extends HTMLElement {
      static ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

      connectedCallback() {
        this.webhookUrl = (this.dataset.webhookUrl || '').trim();
        this.proxyBaseUrl = (this.dataset.proxyBaseUrl || '').trim().replace(/\/+$/, '');
        this.maxFiles = Math.min(Math.max(parseInt(this.dataset.maxFiles || '1', 10) || 1, 1), 2);
        this.maxBytes = parseInt(this.dataset.maxBytes || '2097152', 10) || 2097152;
        this.required = this.dataset.required === 'true';
        this.shop = this.dataset.shop || '';
        this.productId = this.dataset.productId || '';
        this.productHandle = this.dataset.productHandle || '';

        this.fileInput = this.querySelector('[data-file-input]');
        this.dropzone = this.querySelector('[data-dropzone]');
        this.previews = this.querySelector('[data-previews]');
        this.chooseLabel = this.querySelector('[data-choose-label]');
        this.statusEl = this.querySelector('[data-status]');
        this.errorEl = this.querySelector('[data-error]');
        this.errorTextEl = this.querySelector('[data-error-text]');
        this.urlInputs = Array.from(this.querySelectorAll('[data-photo-url-input]')).sort(
          (a, b) => Number(a.dataset.photoIndex || 0) - Number(b.dataset.photoIndex || 0)
        );
        this.sentInput = this.querySelector('[data-photo-sent-input]');

        /** @type {{ id: string, file: File, objectUrl: string, remoteUrl: string, status: string }[]} */
        this.photos = [];
        this.uploading = false;
        this.uploadSeq = 0;

        this.onFileChange = this.onFileChange.bind(this);
        this.onDragOver = this.onDragOver.bind(this);
        this.onDragLeave = this.onDragLeave.bind(this);
        this.onDrop = this.onDrop.bind(this);
        this.onPreviewClick = this.onPreviewClick.bind(this);

        if (this.fileInput) this.fileInput.addEventListener('change', this.onFileChange);
        if (this.dropzone) {
          this.dropzone.addEventListener('dragover', this.onDragOver);
          this.dropzone.addEventListener('dragleave', this.onDragLeave);
          this.dropzone.addEventListener('drop', this.onDrop);
        }
        if (this.previews) this.previews.addEventListener('click', this.onPreviewClick);

        this.updateDropzoneAvailability();
      }

      disconnectedCallback() {
        this.photos.forEach((photo) => URL.revokeObjectURL(photo.objectUrl));
      }

      get strings() {
        return (
          window.photoPersonalizationStrings || {
            choose: 'Escolher ou arrastar foto',
            remove: 'Remover foto',
            uploading: 'Enviando…',
            success: 'Foto enviada com sucesso.',
            success_many: 'Fotos enviadas com sucesso.',
            required: 'Envie sua foto antes de adicionar ao carrinho.',
            invalid_type: 'Envie apenas imagens (JPG, PNG ou WEBP).',
            too_large: 'Cada imagem deve ter no máximo 2 MB.',
            too_many: 'Você pode enviar no máximo %count% foto(s).',
            missing_webhook: 'Upload indisponível no momento. Tente novamente mais tarde.',
            upload_error: 'Não foi possível enviar a foto. Tente novamente.',
          }
        );
      }

      onDragOver(event) {
        event.preventDefault();
        if (this.uploading || this.photos.length >= this.maxFiles) return;
        this.dropzone.classList.add('is-dragover');
      }

      onDragLeave(event) {
        event.preventDefault();
        this.dropzone.classList.remove('is-dragover');
      }

      onDrop(event) {
        event.preventDefault();
        this.dropzone.classList.remove('is-dragover');
        if (this.uploading) return;
        const files = Array.from(event.dataTransfer?.files || []);
        this.addFiles(files);
      }

      onFileChange() {
        const files = Array.from(this.fileInput.files || []);
        this.resetFileInput();
        this.addFiles(files);
      }

      onPreviewClick(event) {
        const button = event.target.closest('[data-remove-photo]');
        if (!button || this.uploading) return;
        const id = button.getAttribute('data-remove-photo');
        this.removePhoto(id);
      }

      addFiles(files) {
        this.clearError();
        if (!files.length) return;

        const slotsLeft = this.maxFiles - this.photos.length;
        if (slotsLeft <= 0) {
          this.showError(this.strings.too_many.replace('%count%', String(this.maxFiles)));
          return;
        }

        if (files.length > slotsLeft) {
          this.showError(this.strings.too_many.replace('%count%', String(this.maxFiles)));
          return;
        }

        const accepted = [];
        for (const file of files) {
          if (!this.isAllowedType(file)) {
            this.showError(this.strings.invalid_type);
            return;
          }
          if (file.size > this.maxBytes) {
            this.showError(this.strings.too_large);
            return;
          }
          accepted.push(file);
        }

        accepted.forEach((file) => {
          const objectUrl = URL.createObjectURL(file);
          this.photos.push({
            id: `photo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            file,
            objectUrl,
            remoteUrl: '',
            status: 'pending',
          });
        });

        this.classList.remove('is-ready');
        this.renderPreviews();
        this.syncHiddenInputs();
        this.updateDropzoneAvailability();
        this.uploadPending();
      }

      removePhoto(id) {
        const index = this.photos.findIndex((photo) => photo.id === id);
        if (index === -1) return;

        const [removed] = this.photos.splice(index, 1);
        if (removed?.objectUrl) URL.revokeObjectURL(removed.objectUrl);

        this.clearError();
        this.renderPreviews();
        this.syncHiddenInputs();
        this.updateDropzoneAvailability();

        const readyCount = this.photos.filter((photo) => photo.remoteUrl).length;
        if (!this.photos.length) {
          this.classList.remove('is-ready');
          this.setStatus('');
        } else if (readyCount === this.photos.length) {
          this.classList.add('is-ready');
          this.setStatus(readyCount > 1 ? this.strings.success_many : this.strings.success, true);
        } else {
          this.classList.remove('is-ready');
          this.setStatus('');
        }
      }

      isAllowedType(file) {
        if (file.type && this.constructor.ALLOWED_TYPES.includes(file.type)) return true;
        const name = (file.name || '').toLowerCase();
        return /\.(jpe?g|png|webp|heic|heif)$/.test(name);
      }

      renderPreviews() {
        if (!this.previews) return;
        this.previews.innerHTML = '';

        if (!this.photos.length) {
          this.previews.hidden = true;
          return;
        }

        this.photos.forEach((photo) => {
          const li = document.createElement('li');
          li.className = 'product-photo-personalization__preview';
          li.dataset.photoId = photo.id;
          if (photo.status === 'uploading') li.classList.add('is-uploading');
          if (photo.status === 'ready') li.classList.add('is-ready');
          if (photo.status === 'error') li.classList.add('is-error');

          const img = document.createElement('img');
          img.src = photo.objectUrl;
          img.alt = photo.file.name;

          const caption = document.createElement('span');
          caption.className = 'product-photo-personalization__preview-name';
          caption.textContent = photo.file.name;

          const removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'product-photo-personalization__remove';
          removeBtn.setAttribute('data-remove-photo', photo.id);
          removeBtn.setAttribute('aria-label', `${this.strings.remove}: ${photo.file.name}`);
          removeBtn.innerHTML =
            '<span aria-hidden="true">&times;</span>';

          const overlay = document.createElement('span');
          overlay.className = 'product-photo-personalization__preview-overlay';
          overlay.hidden = photo.status !== 'uploading';
          overlay.textContent = this.strings.uploading;

          li.appendChild(img);
          li.appendChild(caption);
          li.appendChild(removeBtn);
          li.appendChild(overlay);
          this.previews.appendChild(li);
        });

        this.previews.hidden = false;
      }

      async uploadPending() {
        const pending = this.photos.filter((photo) => !photo.remoteUrl && photo.file);
        if (!pending.length) return;

        if (!this.webhookUrl) {
          pending.forEach((photo) => {
            photo.status = 'error';
          });
          this.renderPreviews();
          this.showError(this.strings.missing_webhook);
          return;
        }

        const seq = ++this.uploadSeq;
        this.setUploading(true);
        this.clearError();
        pending.forEach((photo) => {
          photo.status = 'uploading';
        });
        this.renderPreviews();
        this.setStatus(this.strings.uploading);

        try {
          const formData = new FormData();
          pending.forEach((photo, index) => {
            const filename = photo.file.name || `foto-${index + 1}.jpg`;
            // n8n binary key = field name "images" (images / images0 / …)
            formData.append('images', photo.file, filename);
          });
          formData.append('shop', this.shop);
          formData.append('product_id', this.productId);
          formData.append('product_handle', this.productHandle);
          formData.append('source', 'product-photo-personalization');

          let response;
          try {
            // No custom headers → avoid extra CORS preflight on multipart POST.
            response = await fetch(this.webhookUrl, {
              method: 'POST',
              body: formData,
              mode: 'cors',
              credentials: 'omit',
            });
          } catch (networkError) {
            console.warn('[photo-personalization] network/CORS error', networkError);
            throw new Error(this.strings.upload_error);
          }

          if (seq !== this.uploadSeq) return;

          const rawText = await response.text();
          const payload = this.parseResponseBody(rawText);

          if (!response.ok) {
            const message =
              this.extractUploadError(payload) ||
              `Erro no envio (${response.status}). Tente novamente.`;
            console.warn('[photo-personalization] HTTP error', response.status, rawText.slice(0, 500));
            throw new Error(message);
          }

          if (payload == null) {
            console.warn('[photo-personalization] unparseable body', rawText.slice(0, 500));
            throw new Error(this.strings.upload_error);
          }

          const urls = this.extractUploadUrls(payload);
          if (!urls.length) {
            const message = this.extractUploadError(payload) || this.strings.upload_error;
            console.warn('[photo-personalization] no key/proxy url in payload', payload);
            throw new Error(message);
          }

          // Preview keeps using local File objectUrl; remoteUrl is the proxy for line props.
          pending.forEach((photo, index) => {
            const url = urls[index] || '';
            photo.remoteUrl = url;
            photo.status = url ? 'ready' : 'error';
          });

          this.syncHiddenInputs();
          this.renderPreviews();

          const readyCount = this.photos.filter((photo) => photo.remoteUrl).length;
          if (readyCount === this.photos.length && readyCount > 0) {
            this.classList.add('is-ready');
            this.setStatus(readyCount > 1 ? this.strings.success_many : this.strings.success, true);
          } else {
            this.classList.remove('is-ready');
            this.setStatus('');
          }
        } catch (error) {
          if (seq !== this.uploadSeq) return;
          pending.forEach((photo) => {
            photo.remoteUrl = '';
            photo.status = 'error';
          });
          this.syncHiddenInputs();
          this.renderPreviews();
          this.classList.remove('is-ready');
          this.showError(error.message || this.strings.upload_error);
          this.setStatus('');
        } finally {
          if (seq === this.uploadSeq) this.setUploading(false);
        }
      }

      /**
       * Parse webhook body regardless of Content-Type quirks / double-encoding.
       * @param {string} rawText
       * @returns {unknown}
       */
      parseResponseBody(rawText) {
        const text = (rawText || '').trim();
        if (!text) return null;

        let value;
        try {
          value = JSON.parse(text);
        } catch (error) {
          return null;
        }

        // n8n sometimes returns a JSON string as the body value.
        if (typeof value === 'string') {
          try {
            value = JSON.parse(value);
          } catch (error) {
            return null;
          }
        }

        return value;
      }

      /**
       * Resolve proxy base for GET-by-key. Prefer block setting; else derive from upload host.
       * @returns {string}
       */
      getProxyBaseUrl() {
        if (this.proxyBaseUrl) return this.proxyBaseUrl;
        const upload = (this.webhookUrl || '').replace(/\/+$/, '');
        if (!upload) return '';
        if (/\/product-photo-upload$/i.test(upload)) {
          return upload.replace(/\/product-photo-upload$/i, '/product-photo');
        }
        try {
          const parsed = new URL(upload);
          return `${parsed.origin}/webhook/shopify/product-photo`;
        } catch (error) {
          return '';
        }
      }

      /**
       * Build durable proxy URL from object key (private bucket — not direct storage URL).
       * @param {string} key
       * @returns {string}
       */
      buildProxyUrl(key) {
        const trimmed = String(key || '').trim().replace(/^\/+/, '');
        if (!trimmed) return '';
        const base = this.getProxyBaseUrl();
        if (!base) return '';
        return `${base}?key=${encodeURIComponent(trimmed)}`;
      }

      /**
       * Production n8n shape (primary): flat array of image objects
       *   [{ name, key, url?, uploaded: true }, ...]
       * Prefer building proxy URL from `key`; fall back to `url` when present.
       * When any item has an `uploaded` field, ONLY items with uploaded === true are used.
       * Also accepts a single { key|url } object, and secondarily { images: [...] }.
       * @param {unknown} payload
       * @returns {string[]}
       */
      extractUploadUrls(payload) {
        if (payload == null) return [];

        // Primary: flat array of file/image objects with key and/or url
        if (Array.isArray(payload) && this.isFlatImageList(payload)) {
          return this.urlsFromImageList(payload);
        }

        // Nested array wrapper: [[{ key, ... }, ...]]
        if (Array.isArray(payload) && payload.length === 1 && Array.isArray(payload[0])) {
          if (this.isFlatImageList(payload[0])) {
            return this.urlsFromImageList(payload[0]);
          }
        }

        const root = this.unwrapUploadRoot(payload);
        if (!root || typeof root !== 'object') return [];
        if (root.ok === false) return [];

        // Single image object { key } and/or { url }
        if (!root.images && (typeof root.key === 'string' || typeof root.url === 'string')) {
          if (Object.prototype.hasOwnProperty.call(root, 'uploaded') && root.uploaded !== true) {
            return [];
          }
          const single = this.pickProxyOrUrl(root);
          return single ? [single] : [];
        }

        // Secondary: { images: [...] } (or n8n item { json: { images } })
        let images = root.images;
        if (!images && root.json && typeof root.json === 'object') {
          images = root.json.images;
        }
        if (!images) return [];

        const list = Array.isArray(images) ? images : [images];
        return this.urlsFromImageList(list);
      }

      /**
       * @param {unknown[]} list
       * @returns {boolean}
       */
      isFlatImageList(list) {
        if (!Array.isArray(list) || !list.length) return false;
        // Treat as flat image list when first item looks like a file object (has key/url)
        // and is not an envelope with an images[] property.
        const first = list[0];
        if (!first || typeof first !== 'object' || Array.isArray(first)) return false;
        if (Array.isArray(first.images)) return false;
        return typeof first.url === 'string' || typeof first.name === 'string' || typeof first.key === 'string';
      }

      /**
       * Prefer key → proxy URL; else use url if present (may already be proxy).
       * @param {object|string} item
       * @returns {string}
       */
      pickProxyOrUrl(item) {
        if (!item) return '';
        if (typeof item === 'string') return item.trim();
        if (typeof item !== 'object') return '';

        const key = typeof item.key === 'string' ? item.key.trim() : '';
        if (key) {
          const fromKey = this.buildProxyUrl(key);
          if (fromKey) return fromKey;
        }

        const url = typeof item.url === 'string' ? item.url.trim() : '';
        return url;
      }

      /**
       * @param {unknown[]} list
       * @returns {string[]}
       */
      urlsFromImageList(list) {
        const items = (Array.isArray(list) ? list : []).filter(Boolean);
        const withUploadedFlag = items.some(
          (item) => item && typeof item === 'object' && Object.prototype.hasOwnProperty.call(item, 'uploaded')
        );

        const pick = (item) => this.pickProxyOrUrl(item);

        // If the payload includes an `uploaded` flag, ignore uploaded:false
        // (e.g. stale bucket listing) — do not fall back to those URLs.
        if (withUploadedFlag) {
          return items
            .filter((item) => item && typeof item === 'object' && item.uploaded === true)
            .map(pick)
            .filter(Boolean);
        }

        return items.map(pick).filter(Boolean);
      }

      /**
       * @param {unknown} payload
       * @returns {object|null}
       */
      unwrapUploadRoot(payload) {
        let current = payload;
        // Unwrap [[ {...} ]] etc. (max a few levels) — not used for flat image arrays
        for (let depth = 0; depth < 4; depth++) {
          if (Array.isArray(current)) {
            current = current[0];
            continue;
          }
          break;
        }
        if (!current || typeof current !== 'object') return null;
        return current;
      }

      /**
       * @param {unknown} payload
       * @returns {string}
       */
      extractUploadError(payload) {
        if (Array.isArray(payload) && this.isFlatImageList(payload)) return '';
        const root = this.unwrapUploadRoot(payload);
        if (!root || typeof root !== 'object') return '';
        const nested = root.json && typeof root.json === 'object' ? root.json : null;
        return String(
          root.error || root.message || (nested && (nested.error || nested.message)) || ''
        ).trim();
      }

      syncHiddenInputs() {
        const urls = this.photos.map((photo) => photo.remoteUrl).filter(Boolean);
        this.urlInputs.forEach((input, index) => {
          input.value = urls[index] || '';
        });
        if (this.sentInput) this.sentInput.value = urls.length ? 'sim' : '';
      }

      updateDropzoneAvailability() {
        const full = this.photos.length >= this.maxFiles;
        if (this.dropzone) this.dropzone.classList.toggle('is-full', full);
        if (this.fileInput) this.fileInput.disabled = full || this.uploading;
        if (this.chooseLabel) {
          this.chooseLabel.textContent = this.strings.choose;
        }
      }

      resetFileInput() {
        if (this.fileInput) this.fileInput.value = '';
      }

      setUploading(isUploading) {
        this.uploading = isUploading;
        this.classList.toggle('is-uploading', isUploading);
        this.updateDropzoneAvailability();
      }

      setStatus(message, isSuccess = false) {
        if (!this.statusEl) return;
        if (!message) {
          this.statusEl.hidden = true;
          this.statusEl.textContent = '';
          this.statusEl.classList.remove('is-success');
          return;
        }
        this.statusEl.hidden = false;
        this.statusEl.textContent = message;
        this.statusEl.classList.toggle('is-success', Boolean(isSuccess));
      }

      showError(message) {
        if (!this.errorEl || !this.errorTextEl) return;
        this.errorTextEl.textContent = message;
        this.errorEl.removeAttribute('hidden');
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.setAttribute('hidden', '');
        if (this.errorTextEl) this.errorTextEl.textContent = '';
      }

      /**
       * Used by product-form.js before add-to-cart.
       * @returns {boolean}
       */
      validateForCart() {
        this.clearError();
        if (!this.required) return true;

        if (this.uploading) {
          this.showError(this.strings.uploading);
          this.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }

        const hasUrl = this.photos.some((photo) => (photo.remoteUrl || '').trim() !== '');
        if (hasUrl) return true;

        this.showError(this.strings.required);
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (this.fileInput && !this.fileInput.disabled) this.fileInput.focus();
        return false;
      }
    }
  );
}
